import { useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import { AircraftWbStation, getWbMaxMass } from "../../models/aircraft";
import { checkPointOutOfEnvelope } from "../../models/flightWb";
import {
  buildWbLimitRows,
  buildWbSheetRows,
  fmtMass,
  fmtMoment,
  hasLatEnvelope,
  renderWbEnvelopeSvg,
  WbLoadsheetInput,
  WbSheetRow,
} from "../../utils/wbLoadsheet";
import WbSeatMap from "./WbSeatMap";
import { useWbSheet } from "./useWbSheet";
import "../../css/wb-loadsheet.css";

type WbSheet = ReturnType<typeof useWbSheet>;

export interface WbSheetViewProps {
  sheet: WbSheet;
  canEdit: boolean;
  //planning edits leg 1 fuel in the flight form, so only later legs take fuel here; a last-minute
  //revision edits the actual fuel of every leg
  fuelEditableFromLeg: number;
  defaultViewMode?: "list" | "cabin";
}

function WbSheetView({ sheet, canEdit, fuelEditableFromLeg, defaultViewMode = "cabin" }: WbSheetViewProps) {
  const [viewMode, setViewMode] = useState<"list" | "cabin">(defaultViewMode); //cabin map is the default view
  const { current, legs, sheetLegs, activeLeg, setActiveLeg } = sheet;
  if (!current) return null;
  const input = current.input;
  const aircraft = input.flight.aircraft;
  const hasLat = hasLatEnvelope(aircraft);
  const unit = aircraft.massUnit;
  const { calc, errors, warnings } = current;

  //the view carries its own .wb-sheet scope (the --wb-* tokens), so it renders the same in any host page
  return (
    <div className="wb-sheet">
      {legs.length > 1 && (
        <div className="wb-legtabs mb-3" role="tablist">
          {legs.map((leg, i) => (
            <button
              key={i}
              role="tab"
              aria-selected={i === activeLeg}
              className={"wb-legtab" + (i === activeLeg ? " on" : "") + (sheetLegs[i].errors.length > 0 ? " bad" : "")}
              onClick={(e) => {
                setActiveLeg(i);
                e.currentTarget.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
              }}
            >
              <span className="wb-legtab-n">Leg {i + 1}</span>
              <span className="wb-legtab-route">
                {leg.from.name} → {leg.to.name}
              </span>
              <span className="wb-legtab-status">{sheetLegs[i].errors.length > 0 ? "✕" : "✓"}</span>
            </button>
          ))}
        </div>
      )}

      {/* status band */}
      <div className={"wb-status mb-3 " + (errors.length > 0 ? "bad" : "ok")}>
        <span className="wb-status-big">{errors.length > 0 ? `✕ ${errors.length} limit${errors.length > 1 ? "s" : ""} exceeded` : "✓ Within limits"}</span>
        {errors.length > 0 ? (
          <ul>
            {errors.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        ) : (
          <span className="wb-status-sub wb-num">
            TOW {fmtMass(calc.takeoff.mass)}/{fmtMass(getWbMaxMass(aircraft.wb!))} {unit} · CG {calc.takeoff.arm} mm
            {hasLat ? ` · lat ${calc.takeoff.latArm} mm` : ""} · in envelope
            {warnings.length > 0 && <> · ⚠ {warnings.join(" · ")}</>}
          </span>
        )}
      </div>

      <Row className="gy-3">
        {/* loading area: list or the graphical cabin view */}
        <Col xs={12} lg={8}>
          <div className="d-flex justify-content-end pb-2">
            <div className="wb-seg">
              <button className={viewMode === "list" ? "on" : ""} onClick={() => setViewMode("list")}>
                List
              </button>
              <button className={viewMode === "cabin" ? "on" : ""} onClick={() => setViewMode("cabin")}>
                Cabin
              </button>
            </div>
          </div>
          {viewMode === "list" ? (
            <WbLoadsTable input={input} canEdit={canEdit} setSeatMass={sheet.setSeatMass} setBagMass={sheet.setBagMass} moveSeat={sheet.moveSeat} />
          ) : (
            <WbSeatMap
              aircraft={aircraft}
              wbLoads={input.wbLoads}
              canEdit={canEdit}
              onSwapSeats={sheet.swapSeats}
              onPlaceCargo={sheet.placeCargoOnSeat}
              onRemoveLoad={sheet.removeSeatLoad}
              onSetBagMass={sheet.setBagMass}
            />
          )}
        </Col>

        <Col xs={12} lg={4}>
          {/* limits box */}
          <div className="wb-limits">
            <div className="wb-limits-head">Limits &amp; margins</div>
            {buildWbLimitRows(input).map((r, i) =>
              r.type === "section" ? (
                <div key={i} className="wb-lim-section">
                  {r.label}
                </div>
              ) : (
                <div key={i} className={"wb-lim-row" + (r.ok ? "" : " bad")}>
                  <span className="wb-lim-key">{r.label}</span>
                  <span className="wb-lim-val wb-num">{r.value}</span>
                  <span className="wb-lim-note wb-num">{r.note}</span>
                  <span className="wb-lim-tick">{r.ok ? "✓" : "✕"}</span>
                </div>
              )
            )}
          </div>

          {/* departure fuel + leg time */}
          {(activeLeg >= fuelEditableFromLeg || legs.length > 1) && (
            <WbLegFuelPanel sheet={sheet} canEdit={canEdit} fuelEditable={activeLeg >= fuelEditableFromLeg} />
          )}
        </Col>
      </Row>

      {/* CG envelope charts — same SVG that prints in the briefing pack */}
      <div className={"wb-charts pt-3 " + (hasLat ? "two" : "")} style={!hasLat ? { maxWidth: 660 } : undefined}>
        <WbChartCard title="CG Envelope — Longitudinal" mode="lon" input={input} vbW={330} vbH={hasLat ? 296 : 204} />
        {hasLat && <WbChartCard title="CG Envelope — Lateral" mode="lat" input={input} vbW={330} vbH={296} />}
      </div>

      <div className="wb-footnote mt-3">
        Seat weights from crew/passenger records unless edited · Fuel {aircraft.fuelType} · Trip burn {fmtMass(current.tripFuelMass)} {unit} (
        {aircraft.fuelPerHour} {aircraft.fuelUnit}/h × {legs.length > 1 ? "leg time" : "EET"} {current.eetMins}') · Unusable fuel in BEW.
      </div>
    </div>
  );
}

export default WbSheetView;

// ─── departure fuel & leg time ─────────────────────────────────────────────────

function WbLegFuelPanel({ sheet, canEdit, fuelEditable }: { sheet: WbSheet; canEdit: boolean; fuelEditable: boolean }) {
  const { current, activeLeg, legs, sheetLegs } = sheet;
  const aircraft = current.input.flight.aircraft;
  const tanks = aircraft.wb!.stations.filter((s) => s.type === "fuel");
  const fuelOf = (stationId: string) => current.input.fuelOnBoard.find((f) => f.station.id === stationId);
  const prevLanding = activeLeg > 0 ? sheetLegs[activeLeg - 1].calc.landing.landingFuelOnBoard : undefined;
  const prevLandingTotal = prevLanding?.reduce((acc, f) => acc + f.mass, 0);
  const total = current.input.fuelOnBoard.reduce((acc, f) => acc + f.mass, 0);
  const isUplift = !current.fuelCarried && prevLandingTotal !== undefined;

  return (
    <div className="wb-limits mt-3">
      <div className="wb-limits-head">{legs.length > 1 ? `Leg ${activeLeg + 1} departure` : "Departure fuel"}</div>
      {fuelEditable &&
        tanks.map((tank) => {
          const f = fuelOf(tank.id);
          return (
            <div key={tank.id} className="wb-lim-row">
              <span className="wb-lim-key">{tank.name}</span>
              <span className="wb-lim-val wb-num">
                {canEdit ? (
                  <WbStepper value={Math.round(f?.fuel ?? 0)} emphasized={(f?.fuel ?? 0) > 0} onChange={(v) => sheet.setLegFuel(tank.id, v)} />
                ) : (
                  Math.round(f?.fuel ?? 0)
                )}
              </span>
              <span className="wb-lim-note wb-num">
                {aircraft.fuelUnit} · {fmtMass(f?.mass ?? 0)} {aircraft.massUnit}
              </span>
              <span className="wb-lim-tick" />
            </div>
          );
        })}
      {fuelEditable && (
        <div className="wb-lim-row">
          <span className="wb-lim-key">
            {current.fuelCarried ? "Carried from previous leg" : activeLeg > 0 ? "Uplift at " + legs[activeLeg].from.name : "Actual fuel"}
            {isUplift && <span className="wb-lim-key-sub">landed with {fmtMass(prevLandingTotal!)} {aircraft.massUnit}</span>}
          </span>
          {/* an uplift shows what was added at the stop, next to what the aircraft landed with */}
          <span className="wb-lim-val wb-num">
            {isUplift ? `${total >= prevLandingTotal! ? "+" : ""}${fmtMass(total - prevLandingTotal!)}` : fmtMass(total)}
          </span>
          <span className="wb-lim-note wb-num">{aircraft.massUnit}</span>
          <span className="wb-lim-tick">
            {!current.fuelCarried && canEdit && (
              <button className="wb-personw" title="Back to the planned / carried-over fuel" onClick={sheet.resetLegFuel}>
                ↺
              </button>
            )}
          </span>
        </div>
      )}
      {fuelEditable && prevLandingTotal !== undefined && total < prevLandingTotal && (
        <div className="wb-lim-row bad">
          <span className="wb-lim-key">Below the fuel landed with ({fmtMass(prevLandingTotal)})</span>
          <span className="wb-lim-tick">⚠</span>
        </div>
      )}
      {legs.length > 1 && (
        <div className="wb-lim-row">
          <span className="wb-lim-key">Leg time (min)</span>
          <span className="wb-lim-val wb-num">
            {canEdit ? <WbStepper value={current.eetMins} emphasized onChange={(v) => sheet.setLegEet(v)} /> : current.eetMins}
          </span>
          <span className="wb-lim-note wb-num">{Math.round(legs[activeLeg].distanceNM)} NM</span>
          <span className="wb-lim-tick" />
        </div>
      )}
    </div>
  );
}

// ─── loading table ────────────────────────────────────────────────────────────

interface WbLoadsTableProps {
  input: WbLoadsheetInput;
  canEdit: boolean;
  setSeatMass: (station: AircraftWbStation, mass: number) => void;
  setBagMass: (station: AircraftWbStation, mass: number) => void;
  moveSeat: (stationId: string, dir: -1 | 1) => void;
}

function WbLoadsTable({ input, canEdit, setSeatMass, setBagMass, moveSeat }: WbLoadsTableProps) {
  const aircraft = input.flight.aircraft;
  const hasLat = hasLatEnvelope(aircraft);
  const rows = buildWbSheetRows(input);
  const seatStations = aircraft.wb!.stations.filter((s) => s.type === "seat");

  const stationById = (id?: string) => aircraft.wb!.stations.find((s) => s.id === id);

  //"Reset to person's weight": the occupant's recorded weight, by the person id the load carries
  //(survives seat reordering; two people sharing a last name can't be confused). Loads saved
  //before person ids fall back to the name.
  const flight = input.flight;
  const persons = [flight.pic, flight.sic, flight.studentPilot, flight.deadheadCrew, ...flight.passengers].filter(
    (p): p is NonNullable<typeof p> => p !== undefined
  );
  const personWeight = (row: WbSheetRow) => {
    if (row.personId) return persons.find((p) => p.id === row.personId)?.weight;
    return row.name ? persons.find((p) => p.lastName === row.name)?.weight : undefined;
  };

  const rowFlag = (row: WbSheetRow): boolean => {
    if (row.kind === "zfw") {
      const out = checkPointOutOfEnvelope(aircraft, input.calc.emptyFuel);
      return out.mass || out.arm || out.latArm;
    }
    if (row.kind === "tow") {
      const out = checkPointOutOfEnvelope(aircraft, input.calc.takeoff);
      return out.mass || out.arm || out.latArm || input.calc.takeoff.mass > getWbMaxMass(aircraft.wb!);
    }
    if (row.kind === "ldg") {
      const out = checkPointOutOfEnvelope(aircraft, input.calc.landing);
      return out.mass || out.arm || out.latArm;
    }
    return false;
  };

  const rowClass = (row: WbSheetRow): string => {
    if (row.kind === "zfw") return "wb-total zfw" + (rowFlag(row) ? " wb-flag" : "");
    if (row.kind === "tow") return "wb-total tow" + (rowFlag(row) ? " wb-flag" : "");
    if (row.kind === "ldg") return "wb-total ldg" + (rowFlag(row) ? " wb-flag" : "");
    if (row.kind === "bew" || row.kind === "trip" || row.kind === "fuel") return "wb-sub";
    return "";
  };

  const phaseGlyph = (kind: WbSheetRow["kind"]) =>
    kind === "zfw"
      ? { g: "◇", c: "var(--wb-zfw)" }
      : kind === "tow"
      ? { g: "◆", c: "var(--wb-tow)" }
      : kind === "ldg"
      ? { g: "●", c: "var(--wb-ldg)" }
      : undefined;

  const fuelQty = (stationId?: string) => {
    const f = input.fuelOnBoard.find((x) => x.station.id === stationId);
    return f ? `${Math.round(f.fuel)} ${aircraft.fuelUnit}` : undefined;
  };

  return (
    <div className="wb-table-wrap">
      <table className="wb-table">
        <thead>
          <tr>
            <th className="l" rowSpan={2}>
              Station
            </th>
            <th rowSpan={2}>Weight ({aircraft.massUnit})</th>
            <th className="wb-grp" colSpan={2}>
              Longitudinal
            </th>
            {hasLat && (
              <th className="wb-grp wb-latcol" colSpan={2}>
                Lateral
              </th>
            )}
          </tr>
          <tr>
            <th>Arm (mm)</th>
            <th>Mom (kg·m)</th>
            {hasLat && (
              <>
                <th className="wb-latcol">Lat arm (mm)</th>
                <th>Lat mom</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {(() => {
            const elems: JSX.Element[] = [];
            let lastKind: WbSheetRow["kind"] | undefined;
            const sectionRow = (label: string) => (
              <tr key={"sec-" + label} className="wb-section">
                <td colSpan={hasLat ? 6 : 4}>{label}</td>
              </tr>
            );

            rows.forEach((row) => {
              //separate persons from cargo with section header rows
              if (row.kind === "seat" && lastKind !== "seat") elems.push(sectionRow("Crew & Passengers"));
              if (row.kind === "bag" && lastKind !== "bag") elems.push(sectionRow("Baggage & Cargo"));
              lastKind = row.kind;

              const isSeat = row.kind === "seat";
              const isBag = row.kind === "bag";
              const station = stationById(row.stationId);
              const seatIdx = isSeat ? seatStations.findIndex((s) => s.id === row.stationId) : -1;
              const stationMax = (isSeat || isBag) && station && station.maxMass > 0 ? station.maxMass : undefined;
              const overMax = stationMax !== undefined && row.mass > stationMax;
              const recordedWeight = isSeat ? personWeight(row) : undefined;
              const canResetToPerson = isSeat && canEdit && station && recordedWeight !== undefined && recordedWeight !== row.mass;

              const glyph = phaseGlyph(row.kind);
              elems.push(
                <tr key={row.kind + (row.stationId ?? "")} className={rowClass(row)}>
                  <td className="l">
                    {glyph && <span style={{ display: "inline-block", width: 15, fontWeight: 700, color: glyph.c }}>{glyph.g}</span>}
                    {isSeat ? (
                      /* station = quiet small-caps label, occupant = the hero (matches seat-map chips) */
                      <>
                        <span className="wb-pos">{row.label}</span>
                        <span className="wb-occ-line">
                          {row.name ? <span className="wb-occ">{row.name}</span> : <span className="wb-empty-seat">— empty —</span>}
                          {/* weight was overridden: show the person's recorded weight next to the name; tap to reset */}
                          {canResetToPerson && (
                            <button
                              className="wb-personw"
                              title={`Recorded weight ${recordedWeight} ${aircraft.massUnit} — tap to reset`}
                              onClick={() => setSeatMass(station!, recordedWeight!)}
                            >
                              ({recordedWeight})
                            </button>
                          )}
                          {canEdit && row.mass > 0 && (
                            <span className="wb-reorder">
                              {seatIdx > 0 && (
                                <button onClick={() => moveSeat(row.stationId!, -1)} title="Move up">
                                  <FontAwesomeIcon icon={faCaretUp} />
                                </button>
                              )}
                              {seatIdx < seatStations.length - 1 && (
                                <button onClick={() => moveSeat(row.stationId!, 1)} title="Move down">
                                  <FontAwesomeIcon icon={faCaretDown} />
                                </button>
                              )}
                            </span>
                          )}
                        </span>
                      </>
                    ) : (
                      row.label
                    )}
                    {row.kind === "fuel" && fuelQty(row.stationId) && <span className="wb-station-name"> · {fuelQty(row.stationId)}</span>}
                    {/* fill bar only makes sense when the station HAS a max */}
                    {isBag && canEdit && stationMax !== undefined && (
                      <div className={"wb-fill" + (overMax ? " warn" : "")}>
                        <i style={{ width: `${Math.min(100, (row.mass / stationMax) * 100)}%` }} />
                      </div>
                    )}
                  </td>
                  <td className="n">
                    <span className="wb-wcell">
                      <span className="wb-wcell-controls">
                        {(isSeat || isBag) && canEdit && station ? (
                          <WbStepper value={row.mass} emphasized={row.mass > 0} onChange={(v) => (isSeat ? setSeatMass(station, v) : setBagMass(station, v))} />
                        ) : (isSeat || isBag) && row.mass > 0 ? (
                          <span className="wb-wval-loaded">{fmtMass(row.mass)}</span>
                        ) : (
                          fmtMass(row.mass)
                        )}
                      </span>
                      {stationMax !== undefined && <span className={"wb-max" + (overMax ? " over" : "")}>max {fmtMass(stationMax)}</span>}
                    </span>
                  </td>
                  <td className="n">{row.arm !== undefined ? row.arm : ""}</td>
                  <td className="n">{row.moment !== undefined ? fmtMoment(row.moment) : ""}</td>
                  {hasLat && (
                    <>
                      <td className="n wb-latcol">{row.arm !== undefined ? row.latArm ?? 0 : ""}</td>
                      <td className="n">{row.latMoment !== undefined ? fmtMoment(row.latMoment) : ""}</td>
                    </>
                  )}
                </tr>
              );
            });
            return elems;
          })()}
        </tbody>
      </table>
    </div>
  );
}

export function WbStepper({ value, emphasized, onChange }: { value: number; emphasized?: boolean; onChange: (v: number) => void }) {
  //what is typed, while typing: an emptied box stays empty (not "0" that the next digits append to)
  const [draft, setDraft] = useState<string>();
  return (
    <span className="wb-stepper">
      <button onClick={() => onChange(value - 1)} title="-1">
        −
      </button>
      <input
        type="number"
        min={0}
        className={emphasized ? "nz" : ""}
        value={draft ?? value}
        onChange={(e) => {
          const v = parseInt(e.target.value);
          setDraft(isNaN(v) ? "" : String(v)); //"0369" → "369"
          if (!isNaN(v)) onChange(v);
        }}
        onBlur={() => setDraft(undefined)}
      />
      <button onClick={() => onChange(value + 1)} title="+1">
        +
      </button>
    </span>
  );
}

// ─── chart card ───────────────────────────────────────────────────────────────

function WbChartCard({ title, mode, input, vbW, vbH }: { title: string; mode: "lon" | "lat"; input: WbLoadsheetInput; vbW: number; vbH: number }) {
  const svg = useMemo(
    () =>
      renderWbEnvelopeSvg({
        aircraft: input.flight.aircraft,
        calc: input.calc,
        mode,
        isLatArmInWbPointUp: input.isLatArmInWbPointUp,
        vbW,
        vbH,
      }),
    [input, mode, vbW, vbH]
  );

  const ariaLabel =
    `${title}. Takeoff ${input.calc.takeoff.mass} at arm ${mode === "lat" ? input.calc.takeoff.latArm : input.calc.takeoff.arm}, ` +
    `landing ${input.calc.landing.mass} at arm ${mode === "lat" ? input.calc.landing.latArm : input.calc.landing.arm}. ` +
    (input.errors.length > 0 ? `Out of limits: ${input.errors.join(", ")}` : "Within limits.");

  return (
    <div className="wb-chartbox">
      <div className="wb-chart-head">
        <span className="wb-chart-title">{title}</span>
        <span className="wb-legend">
          <span style={{ color: "var(--wb-tow)" }}>◆ TOW</span>
          <span style={{ color: "var(--wb-ldg)" }}>● LDG</span>
          <span style={{ color: "var(--wb-zfw)" }}>◇ ZFW</span>
          <span style={{ color: "var(--wb-muted)" }}>┄ travel</span>
        </span>
      </div>
      <div role="img" aria-label={ariaLabel} dangerouslySetInnerHTML={{ __html: svg }} />
    </div>
  );
}
